import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import {
  useAttributeGroups,
  useAttributes,
  useCatalogLanguages,
  useDeleteAttribute,
  useMeasurementFamilies,
} from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { ATTRIBUTE_TYPE_LABELS } from '../../model/options';
import { AttributeFormDialog } from '../components/AttributeFormDialog';

/** Attribute dictionary — the tenant's typed, translatable PIM attribute definitions. */
export function AttributesPage() {
  const { t, i18n } = useTranslation('catalog');
  const { canStructureView, canStructureManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  const attributesQuery = useAttributes({ enabled: canStructureView });
  const languagesQuery = useCatalogLanguages({ enabled: canStructureView });
  const measurementFamiliesQuery = useMeasurementFamilies({ enabled: canStructureView });
  const groupsQuery = useAttributeGroups({ enabled: canStructureView });
  const deleteMutation = useDeleteAttribute();

  const allAttributes = useMemo(() => attributesQuery.data ?? [], [attributesQuery.data]);
  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = useMemo(
    () => languagesQuery.data?.find((l) => l.defaultLocale)?.locale,
    [languagesQuery.data],
  );
  // `groupCode` → resolved translated label (plan-catalog-009) — the deprecated free-text `group`
  // string is no longer read for display.
  const groupLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groupsQuery.data ?? []) {
      map.set(
        group.code,
        resolveLocalizedText(group.translations, 'label', i18n.language, group.code, defaultLocale),
      );
    }
    return map;
  }, [groupsQuery.data, i18n.language, defaultLocale]);
  const attributes = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return allAttributes;
    return allAttributes.filter((attribute) => {
      const label = resolveLocalizedText(attribute.translations, 'label', i18n.language, '', defaultLocale);
      return attribute.code.toLowerCase().includes(needle) || label.toLowerCase().includes(needle);
    });
  }, [allAttributes, filter, i18n.language, defaultLocale]);

  // Re-derived from the live query on every render (not a snapshot) so that a mutation performed
  // *while the dialog is open* (e.g. adding a SELECT option) is immediately reflected — see
  // AttributeOptionsManager, nested inside AttributeFormDialog.
  const editing = editingCode ? (allAttributes.find((a) => a.code === editingCode) ?? null) : null;
  const deleting = deletingCode ? (allAttributes.find((a) => a.code === deletingCode) ?? null) : null;

  const openCreate = () => {
    setEditingCode(null);
    setDialogOpen(true);
  };
  const openEdit = (code: string) => {
    setEditingCode(code);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const { code } = deleting;
    deleteMutation.mutate(code, {
      onSuccess: () => {
        success(t('attributes.delete.success', { code }));
        setDeletingCode(null);
      },
      onError: (e) => {
        if (e.status === 409) {
          notifyError(t('attributes.delete.errorInUse'));
        } else {
          notifyError(e.message ?? t('attributes.delete.error'));
        }
        setDeletingCode(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('attributes.title')}
      subtitle={t('attributes.subtitle')}
      canView={canStructureView}
      settingsTo="/settings/catalog"
      settingsLabel={t('attributes.settingsLabel')}
      search={{ value: filter, onChange: setFilter, placeholder: t('attributes.searchPlaceholder') }}
      primaryAction={
        canStructureManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('attributes.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {attributesQuery.isLoading ? (
          <LoadingState label={t('attributes.loading')} />
        ) : attributesQuery.isError ? (
          <ErrorState
            title={t('attributes.loadError.title')}
            message={attributesQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void attributesQuery.refetch()}
          />
        ) : attributes.length === 0 ? (
          <EmptyState
            title={filter ? t('common:empty.noResults') : t('attributes.empty.title')}
            description={
              filter ? t('attributes.empty.filteredDescription') : t('attributes.empty.description')
            }
            icon={<TuneRoundedIcon />}
            action={
              canStructureManage && !filter ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('attributes.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('attributes.table.code')}</TableCell>
                  <TableCell>{t('attributes.table.label')}</TableCell>
                  <TableCell>{t('attributes.table.type')}</TableCell>
                  <TableCell>{t('attributes.table.group')}</TableCell>
                  <TableCell>{t('attributes.table.flags')}</TableCell>
                  <TableCell align="right">{t('attributes.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attributes.map((attribute) => (
                  <TableRow key={attribute.code} hover>
                    <TableCell>
                      <Box
                        component="code"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {attribute.code}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {resolveLocalizedText(
                        attribute.translations,
                        'label',
                        i18n.language,
                        attribute.code,
                        defaultLocale,
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(ATTRIBUTE_TYPE_LABELS[attribute.type] ?? attribute.type)}
                      />
                    </TableCell>
                    <TableCell>
                      {/* `||`, not `??`: an empty-string groupCode (or a group whose label resolves to
                          an empty string) must fall back to the dash — `??` only catches null/undefined. */}
                      {(attribute.groupCode && groupLabelByCode.get(attribute.groupCode)) || '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {attribute.localizable ? (
                          <Chip size="small" label={t('attributes.flags.localizable')} />
                        ) : null}
                        {attribute.searchable ? (
                          <Chip size="small" label={t('attributes.flags.searchable')} />
                        ) : null}
                        <Chip
                          size="small"
                          color={attribute.active ? 'success' : 'default'}
                          label={
                            attribute.active
                              ? t('attributes.flags.active')
                              : t('attributes.flags.inactive')
                          }
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          canStructureManage
                            ? t('attributes.rowActions.edit')
                            : t('attributes.rowActions.view')
                        }
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={t('attributes.rowActions.editAria', { code: attribute.code })}
                          onClick={() => openEdit(attribute.code)}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canStructureManage && (
                        <Tooltip title={t('attributes.rowActions.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('attributes.rowActions.deleteAria', { code: attribute.code })}
                            onClick={() => setDeletingCode(attribute.code)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deletingCode !== null} onClose={() => setDeletingCode(null)}>
        <DialogTitle>{t('attributes.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('attributes.delete.body', { code: deleting?.code })}
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('attributes.delete.inUseHint')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <AttributeFormDialog
        open={dialogOpen}
        editing={editing}
        languages={languagesQuery.data ?? []}
        measurementFamilies={measurementFamiliesQuery.data ?? []}
        canManage={canStructureManage}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
